import DropdownButton from '@shared/components/DropdownButton/DropdownButton';
import { DropdownSelectionProps } from '@shared/components/DropdownButton/DropdownSelection';
import Input from '@shared/components/InputGroup/Input';
import cx from 'classnames';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { ReactComponent as ArrowDown } from 'assets/icons/ArrowDown.svg';

export type RecurrenceOption = 'none' | 'daily' | 'weekly' | 'monthly';

const RRULE_DAYS = ['SU', 'MO', 'TU', 'WE', 'TH', 'FR', 'SA'] as const;

export const serializeRecurrence = (option: RecurrenceOption, scheduledAt: Date | null): string | null => {
  if (option === 'none') return null;
  if (option === 'daily') return 'FREQ=DAILY';
  if (option === 'monthly') return 'FREQ=MONTHLY';
  const day = scheduledAt ? RRULE_DAYS[scheduledAt.getDay()] : 'MO';
  return `FREQ=WEEKLY;BYDAY=${day}`;
};

export const parseRecurrence = (rule: string | null | undefined): RecurrenceOption => {
  if (!rule) return 'none';
  if (rule.startsWith('FREQ=DAILY')) return 'daily';
  if (rule.startsWith('FREQ=WEEKLY')) return 'weekly';
  if (rule.startsWith('FREQ=MONTHLY')) return 'monthly';
  return 'none';
};

// Inverse of serializeRecurrence; named per spec AC-033.
// Returns 'none' when isRecurring is false, otherwise parses the RRULE string.
export const deserializeRecurrence = (
  recurrenceRule: string | null | undefined,
  isRecurring: boolean = !!recurrenceRule
): RecurrenceOption => {
  if (!isRecurring) return 'none';
  return parseRecurrence(recurrenceRule);
};

const OPTIONS: RecurrenceOption[] = ['none', 'daily', 'weekly', 'monthly'];

export const OPTION_LABEL_KEYS: Record<RecurrenceOption, string> = {
  none: 'messenger.schedule_meeting_repeat_none',
  daily: 'messenger.schedule_meeting_repeat_daily',
  weekly: 'messenger.schedule_meeting_repeat_weekly',
  monthly: 'messenger.schedule_meeting_repeat_monthly',
};

interface RecurrenceSelectProps {
  value: RecurrenceOption;
  onChange: (option: RecurrenceOption) => void;
  name: string;
}

const RecurrenceSelect = ({ value, onChange, name }: RecurrenceSelectProps) => {
  const { t } = useTranslation();
  const currentLabel = t(OPTION_LABEL_KEYS[value]);

  const options: DropdownSelectionProps[] = useMemo(
    () =>
      OPTIONS.map((option) => ({
        title: t(OPTION_LABEL_KEYS[option]),
        isSelected: option === value,
        selectionClassName: option === value ? 'bg-gray-5' : undefined,
        onClick: () => onChange(option),
      })),
    [value, onChange, t]
  );

  return (
    <DropdownButton
      id={name}
      options={options}
      placement="bottom-start"
      dropdownContainerClass="my-2.5 w-full !min-w-0"
      dropdownOffset={6}
    >
      {({ open }) => (
        <Input
          value={currentLabel}
          variant="white-bg"
          className="truncate cursor-pointer text-gray-80"
          containerClassName={cx('!border-gray-20', { '!border-primary-50 !ring-gray-30 !ring-4': open })}
          iconClassName={cx('stroke-1 h-5 w-5 stroke-current cursor-pointer', { 'rotate-180': open })}
          icon={ArrowDown}
          autoComplete="off"
          readOnly
        />
      )}
    </DropdownButton>
  );
};

export default RecurrenceSelect;
